import React, { useContext, useState } from 'react';
import { Panel } from 'primereact/panel';
import gql from 'graphql-tag';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import apollo from '../../../services/apollo';
import GpiContactAgreement from './GpiContactAgreement';
import GpiPartnersContactAgreement from './GpiPartnersContactAgreement';
import GpiThirdPartiesContactAgreement from './GpiThirdPartiesContactAgreement';
import UserContactAgreementIntroduction from './CommunicationSettingsIntroduction';

const UPDATE_COMMUNICATION_SETTINGS = gql`
  mutation($input: UpdateCommunicationSettingsInput!) {
    updateCommunicationSettingsForAccount(input: $input) {
      accountId
      agreedToBeContactedByGpi
      gpiContactAgreementUpdatedOn
      agreedToBeContactedByGpiReseller
      gpiResellerContactAgreementUpdatedOn
      agreedToBeContactedByGpiPartners
      gpiPartnersContactAgreementUpdatedOn
    }
  }
`;

const messages = defineMessages({
  successNotification: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
});

const getEdgeForCurrentAccount = (edges, currentAccountId) => {
  let edgeForCurrentAccount;

  edges.forEach((edge) => {
    const accountId = edge.node.id;

    if (accountId === currentAccountId) {
      edgeForCurrentAccount = edge;
    }
  });

  return edgeForCurrentAccount;
};

const CommunicationSettings = ({ userIsViewingOwnPage, accountId, edges }) => {
  const edgeForCurrentAccount = getEdgeForCurrentAccount(edges, accountId);

  const {
    agreedToBeContactedByGpi,
    gpiContactAgreementUpdatedOn,
    agreedToBeContactedByGpiReseller,
    gpiResellerContactAgreementUpdatedOn,
    agreedToBeContactedByGpiPartners,
    gpiPartnersContactAgreementUpdatedOn,
  } = edgeForCurrentAccount;

  const userGdprAgreements = {
    agreedToBeContactedByGpi,
    gpiContactAgreementUpdatedOn,
    agreedToBeContactedByGpiReseller,
    gpiResellerContactAgreementUpdatedOn,
    agreedToBeContactedByGpiPartners,
    gpiPartnersContactAgreementUpdatedOn,
  };

  const [gdprAgreements, setGdprAgreements] = useState(userGdprAgreements);

  const { formatMessage } = useIntl();
  const growl = useContext(NotificationContext);

  if (!userIsViewingOwnPage) return null;

  const updateContactAgreement = (input) =>
    apollo
      .mutate({
        mutation: UPDATE_COMMUNICATION_SETTINGS,
        variables: {
          input,
        },
        refetchQueries: ['getUserById'],
      })
      .then((res) => res.data);

  const updateAgreement = async (event) => {
    const gdprAgreementsToUpdate = { ...gdprAgreements };

    if (event.target.id === 'agreedToBeContactedByGpi') {
      gdprAgreementsToUpdate.agreedToBeContactedByGpi = event.target.checked;
    } else if (event.target.id === 'agreedToBeContactedByGpiReseller') {
      gdprAgreementsToUpdate.agreedToBeContactedByGpiReseller = event.target.checked;
    } else if (event.target.id === 'agreedToBeContactedByGpiPartners') {
      gdprAgreementsToUpdate.agreedToBeContactedByGpiPartners = event.target.checked;
    }

    updateContactAgreement({
      accountId,
      agreeToBeContactedByGpi: gdprAgreementsToUpdate.agreedToBeContactedByGpi,
      agreeToBeContactedByGpiReseller: gdprAgreementsToUpdate.agreedToBeContactedByGpiReseller,
      agreeToBeContactedByGpiPartners: gdprAgreementsToUpdate.agreedToBeContactedByGpiPartners,
    })
      .then((data) => {
        setGdprAgreements(data.updateCommunicationSettingsForAccount);

        growlActionSuccess(growl, formatMessage(messages.successNotification));
      })
      .catch((errors) => {
        growlErrors(growl, errors);
      });
  };

  return (
    <div>
      <Panel
        header={
          <FormattedMessage
            id="user.communication-settings.header"
            defaultMessage="Communication settings & Marketing Consent"
          />
        }
      >
        <UserContactAgreementIntroduction />
        <br />
        <GpiContactAgreement
          updateAgreement={updateAgreement}
          agreedToBeContactedByGpi={gdprAgreements.agreedToBeContactedByGpi}
          gpiContactAgreementUpdatedOn={gdprAgreements.gpiContactAgreementUpdatedOn}
        />
        <br />
        <GpiPartnersContactAgreement
          updateAgreement={updateAgreement}
          agreedToBeContactedByGpiReseller={gdprAgreements.agreedToBeContactedByGpiReseller}
          gpiResellerContactAgreementUpdatedOn={gdprAgreements.gpiResellerContactAgreementUpdatedOn}
        />
        <br />
        <GpiThirdPartiesContactAgreement
          updateAgreement={updateAgreement}
          agreedToBeContactedByGpiPartners={gdprAgreements.agreedToBeContactedByGpiPartners}
          gpiPartnersContactAgreementUpdatedOn={gdprAgreements.gpiPartnersContactAgreementUpdatedOn}
        />
      </Panel>
    </div>
  );
};

export default CommunicationSettings;
